
import { motion } from "framer-motion";
import { RecipeCard } from "./RecipeCard";
import { TextToSpeech } from "./TextToSpeech";

interface Step {
  instruction: string;
  science?: string;
}

interface RecipeStepsProps {
  steps: Step[];
}

// Function to decode HTML entities
const decodeHtmlEntities = (text: string): string => {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.value;
};

export const RecipeSteps = ({ steps }: RecipeStepsProps) => {
  // Filter out any empty steps or steps that just contain a period
  const validSteps = steps.filter(step => 
    step.instruction && 
    step.instruction.trim() !== '.' && 
    step.instruction.trim().length > 0 &&
    step.instruction.trim() !== 'Read Aloud'
  );

  // Animation variants for staggered children
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };

  return (
    <RecipeCard title="Step by Step Instructions" icon="📝">
      <motion.div 
        className="space-y-8"
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        {validSteps.map((step, index) => {
          // Decode HTML entities in the instruction text
          const decodedInstruction = decodeHtmlEntities(step.instruction);
          
          return (
            <motion.div 
              key={index} 
              className="space-y-3"
              variants={itemVariants}
            >
              <div className="flex items-start gap-3">
                <motion.div 
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] text-white font-bold text-base shrink-0 mt-0.5 shadow-md"
                  whileHover={{ scale: 1.1, rotate: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  {index + 1}
                </motion.div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <h4 className="text-base sm:text-lg font-medium text-foreground leading-tight sm:leading-normal">
                      {decodedInstruction}
                    </h4>
                    <div className="shrink-0 mt-0.5">
                      <TextToSpeech text={decodedInstruction} />
                    </div>
                  </div>
                </div>
              </div>
              {step.science && (
                <div className="ml-12 sm:ml-14">
                  <motion.div 
                    className="p-4 bg-[#EDE9FE] rounded-lg border-2 border-[#DDD6FE] shadow-sm"
                    whileHover={{ scale: 1.01, y: -2 }}
                    transition={{ duration: 0.2 }}
                  >
                    <p className="flex items-start gap-2 text-sm sm:text-base leading-relaxed text-[#5B21B6]">
                      <span className="mt-1 shrink-0">🧪</span>
                      <span>{decodeHtmlEntities(step.science)}</span>
                    </p>
                  </motion.div>
                </div>
              )}
            </motion.div>
          );
        })}
      </motion.div>
    </RecipeCard>
  );
};
